import React, { useEffect, useRef, useState } from "react";
import { Card, Row, Col, Badge, Button, Form } from "react-bootstrap";

import { UseOutletContextBrand } from "./Brand";
import { StateLanguage } from "../../../../languages/config/StateLanguage";
import { BrandDA } from "../../../../services/admin/brands.inventory.service";
import { Error } from "../../../../models/error.model";
import { BrandsData, Brand } from "../../../../models/brands.inventory.model";
import { GlobalSwal } from "../../../../styles/swal.style";
import { getError, getMessage } from "../../../../languages/translations/response";
import { errorWebHandler } from "../../../../scripts/error.web.handler.script";
import { modalShow } from "../../../../utils/modal.util";

import AppModalBrand from "../../../../components/modal/ModalBrand";
import AppPreloader from "../../../../components/Preloader";
import AppPagination from "../../../../components/element/ElementPagination";

export interface BrandListPageProps {}

let errorResponse: Error;

const BrandListPage: React.FunctionComponent<BrandListPageProps> = () => {
  const { setRoute, companyForUser, setBrandsForCompany } = UseOutletContextBrand();
  const { lang } = StateLanguage();

  const [mounted, setMounted] = useState(false);
  const [brands, setBrands] = useState<Brand[] | null>(null);
  const [brandItem, setBrandItem] = useState<Brand | null>(null);

  // ─── Pagination ──────────────────────────────────────────────────
  const pageRef = useRef(1);
  const limitRef = useRef(10);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    pages: 0,
    total: 0,
  });

  // ─── Load brands ───────────────────────────────────────────────────────

  const loadBrands = async (id_company: string, page: number, limit: number) => {
    if (!id_company) return;

    pageRef.current = page;
    limitRef.current = limit;

    await BrandDA.getBrands(id_company, page, limit)
      .then((response) => {
        if (response.status === 200) {
          const { items, pagination: pg } = response.data as BrandsData;
          setBrands(items ?? []);
          setPagination({
            page: pg?.page ?? page,
            limit: pg?.limit ?? limit,
            pages: pg?.pages ?? 0,
            total: pg?.total ?? 0,
          });
        } else {
          errorResponse = response.data;
          GlobalSwal.fire({
            title: getError(errorResponse.code, lang.code, errorResponse.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: "error",
            confirmButtonText: lang.labels.okGotIt,
          });
        }
      })
      .catch((error) => errorWebHandler(lang, error));
  };

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > pagination.pages) return;
    if (companyForUser) loadBrands(companyForUser.id, newPage, limitRef.current);
  };

  // ─── Abrir modal crear ─────────────────────────────────────────────────────
  const openCreateModal = () => {
    setBrandItem(null);
    modalShow("modal-brand");
  };

  // ─── Abrir modal editar ────────────────────────────────────────────────────
  const openEditModal = async (id: string) => {
    if (!companyForUser) return;
    modalShow("modal-brand");

    await BrandDA.getBrand(companyForUser.id, id)
      .then((response) => {
        if (response.status === 200) {
          setBrandItem(response.data as Brand);
        } else {
          errorResponse = response.data;
          GlobalSwal.fire({
            title: getError(errorResponse.code, lang.code, errorResponse.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: "error",
            confirmButtonText: lang.labels.okGotIt,
          });
        }
      })
      .catch((error) => errorWebHandler(lang, error));
  };


  const handleModalSuccess = (_mode: "create" | "edit") => {
    if (companyForUser) {
      loadBrands(companyForUser.id, pageRef.current, limitRef.current);
    }
  };

  // ─── Delete Brand ────────────────────────────────────────────────────────
  const submitBrandDelete = async (id: string) => {
    if (!companyForUser) return;

    GlobalSwal.fire({
      title: lang.labels.youWantToDeleteThisBrand,
      text: lang.labels.youWillNotBeAbleToUndo,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: lang.labels.yesDelete,
      cancelButtonText: lang.labels.noCancel,
    }).then(async (result) => {
      if (!result.isConfirmed) return;

      await BrandDA.deleteBrands(companyForUser.id, id)
        .then((response) => {
          if (response.status === 200) {
            // Si era el último item de la página, retroceder una página
            const isLastItemOnPage = brands?.length === 1 && pageRef.current > 1;
            const targetPage = isLastItemOnPage ? pageRef.current - 1 : pageRef.current;

            loadBrands(companyForUser.id, targetPage, limitRef.current);

            GlobalSwal.fire({
              title: getMessage(response.data.message ?? "Successfully deleted brand", lang.code),
              text: lang.labels.actionCompletedReturningToPage,
              icon: "success",
              showConfirmButton: false,
              timer: 2200,
            });
          } else {
            errorResponse = response.data;
            GlobalSwal.fire({
              title: getError(errorResponse.code, lang.code, errorResponse.message),
              text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
              icon: "error",
              confirmButtonText: lang.labels.okGotIt,
            });
          }
        })
        .catch((error) => errorWebHandler(lang, error));
    });
  };


  const statusBadge = (is_active: boolean) => (is_active ? <Badge bg="success">Activo</Badge> : <Badge bg="secondary">Inactivo</Badge>);


  useEffect(() => {
    if (companyForUser) {
      setBrands(null);
      loadBrands(companyForUser.id, 1, 10);
    }
  }, [companyForUser]);


  useEffect(() => {
    setMounted(true);
    setRoute({
      path: { root: lang.labels.brands, branch: lang.labels.listBrands },
      company: true,
    });
    return () => setMounted(false);
  }, []);

  if (!mounted) return null;

  return (
    <div className="w-100 h-100">
      {brands ? (
        <>
          {/* ── Header ──────────────────────────────────────────────────── */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>{lang.labels.brands}</h2>
            <Button variant="primary" onClick={openCreateModal}>
              {lang.labels.createBrand}
            </Button>
          </div>

          {/* ── Grid de Cards ────────────────────────────────────────────── */}
          <Row xs={1} md={2} lg={3} xl={4} className="g-4 mb-4">
            {brands.map((item) => (
              <Col key={item.id}>
                <Card className="h-100 shadow-sm">
                  <Card.Body className="d-flex flex-column align-items-center text-center">
                    {/* Logo */}
                    <div className="mb-3" style={{ width: "100px", height: "100px" }}>
                      {item.logo ? (
                        <img
                          src={item.logo}
                          alt={item.name}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                          }}
                          onError={(e) => (e.currentTarget.style.display = "none")}
                        />
                      ) : (
                        <div className="bg-light d-flex align-items-center justify-content-center h-100 rounded">
                          <span className="text-muted">Sin logo</span>
                        </div>
                      )}
                    </div>

                    <h5 className="mb-2">{item.name}</h5>
                    <div className="mb-3">{statusBadge(item.is_active)}</div>

                    {/* Actions */}
                    <div className="mt-auto d-flex gap-2">
                      <Button variant="outline-primary" size="sm" onClick={() => openEditModal(item.id)}>
                        {lang.labels.edit}
                      </Button>
                      <Button variant="outline-secondary" size="sm" onClick={() => submitBrandDelete(item.id)}>
                        {lang.labels.delete}
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          {brands.length === 0 && <div className="text-center text-muted mt-5">{lang.labels.noRecordsFound}</div>}

          {/* ── Pagination ───────────────────────────────────────────────── */}
              <AppPagination pagination={pagination} onPageChange={handlePageChange} />
        </>
      ) : (
        <AppPreloader />
      )}

      {/* ── Modal ─────────────────────────────────────────────────────────── */}
      <AppModalBrand companyForUser={companyForUser} brandItem={brandItem} onSuccess={handleModalSuccess} />
    </div>
  );
};

export default BrandListPage;
